import React, { useState, useEffect } from 'react';
import { ArrowUpRight, Filter, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async'; 

const Projects = () => {
  const [filter, setFilter] = useState("All");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const categories = ["All", "Residential", "Commercial", "Renovation"];

  // Hero Section ke liye changing images
  const heroSliderImages = [
    "https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&q=80",
    "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&q=80",
    "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&q=80",
    "https://images.unsplash.com/photo-1613545325278-f24b0cae1224?auto=format&fit=crop&q=80"
  ];

  // Auto-change logic (Har 4 second mein)
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroSliderImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const projects = [
    { id: 1, title: "The Onyx Villa", cat: "Residential", location: "South Delhi", img: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&q=80" },
    { id: 2, title: "Velvet Gold Lounge", cat: "Commercial", location: "Mumbai", img: "https://images.unsplash.com/photo-1551516594-56cb78394645?auto=format&fit=crop&q=80" },
    { id: 3, title: "Royal Master Bedroom", cat: "Residential", location: "Gurgaon", img: "https://images.unsplash.com/photo-1616137466211-f939a420be84?auto=format&fit=crop&q=80" },
    { id: 4, title: "Amber Corporate", cat: "Commercial", location: "Noida", img: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80" },
    { id: 5, title: "Golden Era Kitchen", cat: "Renovation", location: "Bangalore", img: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&q=80" },
    { id: 6, title: "The Marble Hall", cat: "Residential", location: "Jaipur", img: "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&q=80" },
  ];

  const filteredProjects = filter === "All" ? projects : projects.filter(p => p.cat === filter);

  return (
    <div className="bg-zinc-50 min-h-screen font-sans selection:bg-amber-900 selection:text-white">
      
      <Helmet>
        <title>Portfolio | Luxuriya Homes</title>
      </Helmet>

      {/* ================= 1. HERO SECTION (Split Layout with Slider) ================= */}
      <section className="bg-zinc-900 text-white relative pt-32 pb-20 px-6 md:px-12 overflow-hidden">
        
        {/* Background Accents */}
        <div className="absolute top-0 right-0 w-1/3 h-full bg-zinc-800/20 skew-x-12 pointer-events-none"></div>

        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative z-10">
            
            {/* Left Side: Text */}
            <motion.div 
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
            >
                <div className="flex items-center gap-3 mb-6">
                    <div className="h-[1px] w-12 bg-amber-500"></div>
                    <span className="text-amber-500 uppercase tracking-[0.2em] text-xs font-bold">Our Portfolio</span>
                </div>

                <h1 className="text-5xl md:text-7xl font-serif mb-8 leading-tight">
                    Crafting <br />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-200 to-amber-600">Timeless</span> Spaces
                </h1>

                <p className="text-zinc-400 text-lg font-light leading-relaxed max-w-md mb-10">
                    Explore our finest works, where every corner tells a story of luxury and architectural precision.
                </p>

                <div className="flex gap-12 border-t border-zinc-800 pt-8">
                    <div>
                        <h3 className="text-3xl font-serif text-white">150+</h3>
                        <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Projects</p>
                    </div>
                    <div>
                        <h3 className="text-3xl font-serif text-white">100%</h3>
                        <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Satisfaction</p>
                    </div>
                </div>
            </motion.div>

            {/* Right Side: AUTO-CHANGING IMAGE */}
            <div className="relative hidden lg:block h-[500px] w-full bg-zinc-800 rounded-sm overflow-hidden border border-zinc-700 shadow-2xl">
                <AnimatePresence mode="wait">
                    <motion.img 
                        key={currentImageIndex}
                        src={heroSliderImages[currentImageIndex]}
                        initial={{ opacity: 0, scale: 1.1 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 1.2 }}
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                </AnimatePresence>
                
                {/* Image Indicator Dots */}
                <div className="absolute bottom-6 left-6 flex gap-2 z-20">
                    {heroSliderImages.map((_, i) => (
                        <div 
                          key={i} 
                          className={`h-1 transition-all duration-500 ${currentImageIndex === i ? "w-8 bg-amber-500" : "w-3 bg-white/30"}`}
                        />
                    ))}
                </div>

                <div className="absolute bottom-0 right-0 bg-white text-black px-6 py-4 z-20">
                    <ArrowUpRight size={24} />
                </div>
            </div>
        </div>
      </section>

      {/* ================= 2. FILTER BAR (Sticky) ================= */}
      <section className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-zinc-200 py-4 px-4 shadow-sm">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2 text-zinc-500">
                <Filter size={14} />
                <span className="text-[15px] uppercase tracking-widest font-bold">Category:</span>
            </div>

            <div className="flex gap-2 md:gap-4 overflow-x-auto w-full md:w-auto no-scrollbar">
  {categories.map((cat) => (
    <button 
      key={cat}
      onClick={() => setFilter(cat)}
      className={`whitespace-nowrap px-6 py-2 text-[10px] uppercase tracking-widest 
        transition-all duration-300 border 
        ${
          filter === cat 
          ? "bg-zinc-900 text-zinc-100 border-zinc-800 shadow-lg" 
          : "bg-zinc-950 text-zinc-400 border-zinc-800 hover:bg-zinc-900 hover:text-zinc-100"
        }`}
    >
      {cat}
    </button>
  ))}
</div>

            
            {/* <div className="hidden md:flex items-center gap-2 text-[10px] font-bold text-zinc-900 uppercase tracking-widest cursor-pointer hover:text-amber-700">
                <span>Newest Works</span>
                <ChevronDown size={14} />
            </div> */}
        </div>
      </section>

      {/* ================= 3. GALLERY GRID ================= */}
      <section className="py-20 px-4 max-w-7xl mx-auto min-h-screen">
        <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-16 gap-x-10">
            <AnimatePresence>
                {filteredProjects.map((project, index) => (
                    <motion.div 
                        layout
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ delay: index * 0.05 }}
                        key={project.id} 
                        className="group cursor-pointer"
                    >
                        <div className="relative h-[420px] overflow-hidden mb-6 bg-zinc-100 shadow-sm">
                            <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-all duration-500 z-10"></div>
                            <img 
                                src={project.img} 
                                alt={project.title} 
                                className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                            />
                            <div className="absolute bottom-4 left-4 z-20">
                                <span className="bg-white/90 backdrop-blur px-3 py-1 text-[9px] uppercase tracking-[0.2em] font-bold text-zinc-900">
                                    {project.cat}
                                </span>
                            </div>
                        </div>
                        
                        <div className="flex justify-between items-start border-b border-zinc-100 pb-5 group-hover:border-amber-600 transition-colors duration-500">
                            <div>
                                <h4 className="font-serif text-2xl text-zinc-900 mb-1 group-hover:text-amber-800 transition-colors">{project.title}</h4>
                                <p className="text-[11px] text-zinc-400 flex items-center gap-1 uppercase tracking-widest">
                                    {project.location}
                                </p>
                            </div>
                            <div className="w-10 h-10 rounded-full border border-zinc-100 flex items-center justify-center text-zinc-300 group-hover:bg-amber-800 group-hover:text-white group-hover:border-amber-800 transition-all duration-500">
                                <ArrowUpRight size={18} />
                            </div>
                        </div>
                    </motion.div>
                ))}
            </AnimatePresence>
        </motion.div>
      </section>

      {/* ================= 4. FOOTER CTA ================= */}
      <section className="bg-zinc-900 py-24 text-center text-white relative">
        <div className="max-w-3xl mx-auto px-4 relative z-10">
            <h2 className="text-4xl md:text-5xl font-serif mb-8 leading-snug">Let's Design Your <span className="italic text-amber-500">Dream Space</span> Together</h2>
            <button className="bg-amber-700 text-white px-12 py-5 uppercase tracking-[0.25em] text-[10px] font-bold hover:bg-amber-600 transition-all shadow-xl">
                Get a Free Quote
            </button>
        </div>
        {/* Background Decorative Text */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden opacity-[0.03] pointer-events-none select-none">
            <h1 className="text-[11vw] font-serif whitespace-nowrap -mb-10">LUXURIYA HOMES</h1>
        </div>
      </section>

    </div>
  );
};

export default Projects;